// Date filter for the live lists: a preset select (any time, last 30/90 days,
// custom) that fills two hidden fields with ISO dates, which the list
// endpoints parse whatever the server's culture. "Custom range" shows two
// flatpickr inputs, the same calendar as the job form. Sits inside a liveList, which it asks to reload with a
// `live-filter` event; after the list swaps (paging, Back) it re-reads the
// hidden fields so the select matches the results.
//
//   <div x-data="dateRange">
//     <select x-model="preset" @change="choose()" data-live-ignore>…</select>
//     <input type="hidden" name="StartDate" value="2026-09-30" x-ref="start" />
//     <input type="hidden" name="EndDate" value="2026-10-29" x-ref="end" />
//     <input type="text" x-ref="from" data-live-ignore /> (and "to")
//   </div>

import flatpickr from 'flatpickr';
import { enhanceDate } from './enhance/dates';

const PRESETS = ['30', '90'];

function iso(date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
}

function daysAgo(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return iso(date);
}

export default () => ({
  preset: '',
  from: '',
  to: '',

  init() {
    // The pickers live on the inputs (input._flatpickr), not in Alpine's reactive data.
    enhanceDate(this.$refs.from, { onChange: ([date]) => this.picked('from', date) });
    enhanceDate(this.$refs.to, { onChange: ([date]) => this.picked('to', date) });
    this.read();
    window.addEventListener('live-swapped', () => this.read());
  },

  // From the hidden fields, without reloading.
  read() {
    this.from = this.$refs.start.value;
    this.to = this.$refs.end.value;
    this.preset = this.presetFor(this.from, this.to);
    this.$refs.from._flatpickr.setDate(this.from || null, false, 'Y-m-d');
    this.$refs.to._flatpickr.setDate(this.to || null, false, 'Y-m-d');
    this.limit();
  },

  // The end can't come before the start, nor the start after the end.
  limit() {
    // As dates: the pickers would read ISO strings as dd/mm/yyyy.
    const date = (value) => (value ? flatpickr.parseDate(value, 'Y-m-d') : null);
    this.$refs.from._flatpickr.set('maxDate', date(this.to));
    this.$refs.to._flatpickr.set('minDate', date(this.from));
  },

  picked(which, date) {
    this[which] = date ? flatpickr.formatDate(date, 'Y-m-d') : '';
    this.limit();
    this.customChanged();
  },

  presetFor(from, to) {
    if (!from && !to) {
      return '';
    }

    const preset = PRESETS.find((days) => to === iso(new Date()) && from === daysAgo(Number(days) - 1));
    return preset || 'custom';
  },

  choose() {
    if (this.preset === 'custom') {
      return;
    }

    this.from = this.preset ? daysAgo(Number(this.preset) - 1) : '';
    this.to = this.preset ? iso(new Date()) : '';
    this.apply();
  },

  // Both dates go together: the job list only reads the end date when there's a start date.
  apply() {
    const complete = this.from && this.to;
    this.$refs.start.value = complete ? this.from : '';
    this.$refs.end.value = complete ? this.to : '';
    this.$dispatch('live-filter');
  },

  customChanged() {
    if ((this.from && this.to) || (!this.from && !this.to)) {
      this.apply();
    }
  },
});
